import {useAtom} from 'jotai';
import {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {useBackgroundClick} from '@/hooks';
import {mobileBottomSheetTopAtom} from '@/map/atom';
import {RADIUS_OPTIONS} from '@/map/constants';
import {distanceToString} from '@/map/utils';
import {interceptProps} from '@/styled-components';
import {palette} from '@/styles';
import Arrow from './radius-arrow.svg';

interface IProps {
  radius: number;
  onChangeRadius(radius: number): void;
}

export const RadiusSelector = ({radius, onChangeRadius}: IProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [sheetTop, setSheetTop] = useAtom(mobileBottomSheetTopAtom);

  const onClick = (nextRadius: number) => () => {
    if (nextRadius !== radius) {
      onChangeRadius(nextRadius);
    }

    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      const dropdownHeight = dropdownRef.current?.offsetHeight;
      const offsetTop = 84;

      if (dropdownHeight && sheetTop < dropdownHeight + offsetTop) {
        setSheetTop(dropdownHeight + offsetTop);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useBackgroundClick(dropdownRef, () => {
    setOpen(false);
  });

  return (
    <Root>
      <Button active={open} onClick={() => setOpen((prev) => !prev)}>
        반경{' '}
        {distanceToString(radius, {
          roundingMode: 'ceil',
        })}
        <img
          src={Arrow}
          style={{transform: `rotate(${open ? 180 : 0}deg)`}}
          alt="열기"
        />
      </Button>
      {open && (
        <>
          <Backdrop />
          <SelectMenu ref={dropdownRef}>
            {RADIUS_OPTIONS.map(
              (i) =>
                i !== 'reset' && (
                  <SelectItem
                    key={i}
                    onClick={onClick(i)}
                    active={i === radius}
                  >
                    반경 {distanceToString(i, {roundingMode: 'ceil'})}
                  </SelectItem>
                )
            )}
          </SelectMenu>
        </>
      )}
    </Root>
  );
};

const Root = styled.div``;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const Button = styled('button').withConfig({
  shouldForwardProp: interceptProps(['active']),
})<{
  active?: boolean;
}>`
  display: flex;
  align-items: center;
  height: 32px;
  padding: 2px 2px 2px 12px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  background-color: ${palette.white};
  color: ${palette.teal100};
  border: solid 1px ${palette.teal100};
  cursor: pointer;
  ${({active}) =>
    active &&
    `
    background-color: ${palette.white};
  `}
`;

const SelectMenu = styled.div`
  border-radius: 4px;
  background-color: ${palette.white};
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.2);
  width: 280px;
  position: absolute;
  top: 48px;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  padding: 0px;
  border: 1px solid #f5f5f5;
  border-right: 0;
  border-radius: 4px;
`;

const SelectItem = styled('button').withConfig({
  shouldForwardProp: interceptProps(['active']),
})<{
  active?: boolean;
}>`
  flex-shrink: 0;
  height: 44px;
  border-bottom: 1px solid #f5f5f5;
  border-right: 1px solid #f5f5f5;
  font-size: 12px;
  font-weight: 500;
  ${({active}) => active && `color: ${palette.teal100};`}
`;
